import { useState, useEffect, useRef } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { MapPin, Users, Clock, Flame, GlassWater, Music, Calendar, CalendarDays, Heart, Camera, Video, Play, ThumbsUp, ThumbsDown, DollarSign, ChevronDown, Ban, X, ZoomIn, ChevronLeft, ChevronRight, Globe } from "lucide-react";
import { SiInstagram, SiTiktok } from "react-icons/si";
import type { CapacitySource, Venue, VenueEvent } from "@shared/schema";
import { isVenueOpen, hasNoCapacityData, type OpeningHoursData as SharedOpeningHoursData } from "@shared/opening-hours";
import { useUpdateVenue, useVenue } from "@/hooks/use-venues";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useVenueSpecials, useRedeemSpecial, useHoneyBalance } from "@/hooks/use-honey";
import { useAuth } from "@/hooks/use-auth";
import { timeAgo } from "@/lib/time-ago";
import { getCapacityLabel, getCapacityLabelTextClass, getCapacityChipClass, getCapacityIconKind } from "@/lib/capacity-utils";

interface VenueDetailDrawerProps {
  onUserClick?: (userId: number) => void;
  venue: Venue | null;
  isOpen: boolean;
  onClose: () => void;
  onAnimationEnd?: (open: boolean) => void;
}

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TAG_COLORS: Record<string, string> = {
  "Drinks Deal": "bg-blue-500/20 text-blue-400 border-blue-500/30",
  "Food Deal": "bg-primary/20 text-primary border-primary/30",
  "Entertainment": "bg-purple-500/20 text-purple-400 border-purple-500/30",
  "Live Music": "bg-pink-500/20 text-pink-400 border-pink-500/30",
  "Ladies Night": "bg-fuchsia-500/20 text-fuchsia-400 border-fuchsia-500/30",
  "DJ Night": "bg-violet-500/20 text-violet-400 border-violet-500/30",
  "Happy Hour": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Brunch": "bg-orange-500/20 text-orange-400 border-orange-500/30",
};

const VIBE_OPTIONS = ["Chill", "Lively", "Buzzing", "Packed", "Electric"];

function compressImage(file: File, maxWidth = 800, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export function PriceMeter({ priceLevel }: { priceLevel: string | null | undefined }) {
  if (!priceLevel) {
    return (
      <span className="inline-flex items-center gap-0.5 text-zinc-500 text-xs" data-testid="price-meter-unknown">
        <span>$ not available</span>
      </span>
    );
  }
  const levelMap: Record<string, number> = {
    PRICE_LEVEL_FREE: 0,
    PRICE_LEVEL_INEXPENSIVE: 1,
    PRICE_LEVEL_MODERATE: 2,
    PRICE_LEVEL_EXPENSIVE: 3,
    PRICE_LEVEL_VERY_EXPENSIVE: 4,
  };
  const level = levelMap[priceLevel] ?? 0;
  if (level === 0) return null;
  const max = 4;
  return (
    <span className="inline-flex items-center gap-0" data-testid="price-meter">
      {Array.from({ length: max }, (_, i) => (
        <DollarSign
          key={i}
          className={`w-3.5 h-3.5 ${i < level ? 'text-emerald-400' : 'text-zinc-700'}`}
        />
      ))}
    </span>
  );
}

interface OpeningPeriod {
  open: { day: number; hour: number; minute: number };
  close?: { day: number; hour: number; minute: number };
}

interface OpeningHoursData {
  weekdayDescriptions?: string[];
  periods?: OpeningPeriod[];
}

function formatCloseTime(hour: number, minute: number): string {
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  const displayMin = minute > 0 ? `:${String(minute).padStart(2, '0')}` : '';
  return `${displayHour}${displayMin}${ampm}`;
}

function getOpenClosedStatus(openingHours: OpeningHoursData | null | undefined): { isOpen: boolean; label: string } | null {
  if (!openingHours?.periods || openingHours.periods.length === 0) return null;

  const now = new Date();
  const currentDay = now.getDay();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  const previousDay = (currentDay + 6) % 7;

  for (const period of openingHours.periods) {
    if (!period.close) {
      if (period.open.day === currentDay) return { isOpen: true, label: "Open 24 hours" };
      continue;
    }

    const openDay = period.open.day;
    const closeDay = period.close.day;
    const openTime = period.open.hour * 60 + period.open.minute;
    const closeTime = period.close.hour * 60 + period.close.minute;

    if (openDay === closeDay && openDay === currentDay) {
      if (currentTime >= openTime && currentTime < closeTime) {
        return { isOpen: true, label: `Open til ${formatCloseTime(period.close.hour, period.close.minute)}` };
      }
    }

    if (openDay === currentDay && closeDay !== currentDay) {
      if (currentTime >= openTime) {
        return { isOpen: true, label: `Open til ${formatCloseTime(period.close.hour, period.close.minute)}` };
      }
    }

    if (openDay === previousDay && closeDay === currentDay) {
      if (currentTime < closeTime) {
        return { isOpen: true, label: `Open til ${formatCloseTime(period.close.hour, period.close.minute)}` };
      }
    }
  }

  let nextOpen: { hour: number; minute: number; day: number } | null = null;
  for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
    const checkDay = (currentDay + dayOffset) % 7;
    const dayPeriods = openingHours.periods.filter(p => p.open.day === checkDay);
    for (const period of dayPeriods) {
      const openTime = period.open.hour * 60 + period.open.minute;
      if (dayOffset === 0 && openTime > currentTime) {
        return { isOpen: false, label: `Closed til ${formatCloseTime(period.open.hour, period.open.minute)}` };
      }
      if (dayOffset > 0 && !nextOpen) {
        nextOpen = { ...period.open, day: checkDay };
      }
    }
  }

  if (nextOpen) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return { isOpen: false, label: `Closed til ${days[nextOpen.day]} ${formatCloseTime(nextOpen.hour, nextOpen.minute)}` };
  }

  return { isOpen: false, label: "Closed" };
}

export function VenueDetailDrawer({ venue: venueProp, isOpen, onClose, onUserClick, onAnimationEnd }: VenueDetailDrawerProps) {
  const updateVenue = useUpdateVenue();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reportCapacity, setReportCapacity] = useState(50);
  const [reportVibe, setReportVibe] = useState("Lively");
  const [mediaType, setMediaType] = useState<"photo" | "video">("photo");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [showMapsPicker, setShowMapsPicker] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [showHours, setShowHours] = useState(false);
  const lastTrackedId = useRef<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  function clearMedia() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    setVideoDuration(null);
    setVideoError(null);
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (videoInputRef.current) videoInputRef.current.value = "";
  }

  const { user: authUser, isAuthenticated } = useAuth();

  const { data: fullVenue } = useVenue(isOpen && venueProp ? venueProp.id : 0);
  const venue = fullVenue ?? venueProp;

  const { data: events, isLoading: eventsLoading } = useQuery<VenueEvent[]>({
    queryKey: [`/api/venues/${venue?.id}/events`],
    enabled: !!venue,
  });

  type UpcomingEventItem = {
    id: number;
    title: string;
    description: string | null;
    startTime: string;
    endTime: string | null;
    ticketUrl: string | null;
    source: string;
    raVenueName: string | null;
    venueId: number | null;
    venueName: string | null;
    venueLat: number | null;
    venueLng: number | null;
  };
  const { data: upcomingEvents } = useQuery<UpcomingEventItem[]>({
    queryKey: ["venue-events", venue?.id],
    queryFn: async () => {
      const res = await fetch(
        `/api/events?venueId=${venue!.id}&filter=this_week`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`Failed to load events (${res.status})`);
      const json = await res.json();
      return Array.isArray(json) ? (json as UpcomingEventItem[]) : [];
    },
    enabled: !!venue && venue.id > 0,
    staleTime: 5 * 60 * 1000,
  });

  function formatUpcomingDateTime(iso: string): string {
    const d = new Date(iso);
    const day = d.toLocaleDateString("en-GB", { weekday: "short" });
    const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    let time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true });
    time = time.toLowerCase().replace(/\s+/g, "").replace(":00", "");
    return `${day} ${date} · ${time}`;
  }

  const { data: favCheck } = useQuery<{ isFavourite: boolean }>({
    queryKey: ['/api/favourites', venue?.id, 'check'],
    queryFn: () => fetch(`/api/favourites/${venue?.id}/check`, { credentials: 'include' }).then(r => r.ok ? r.json() : { isFavourite: false }),
    enabled: !!venue,
  });

  const isFavourite = favCheck?.isFavourite ?? false;

  const { data: specials } = useVenueSpecials(venue?.id ?? 0);
  const { data: honeyBalance } = useHoneyBalance();
  const redeemMutation = useRedeemSpecial();

  const [prefsDrawerOpen, setPrefsDrawerOpen] = useState(false);
  const [prefsEvents, setPrefsEvents] = useState(true);
  const [prefsThreshold, setPrefsThreshold] = useState(false);
  const [prefsThresholdType, setPrefsThresholdType] = useState<"quiet" | "busy" | "both">("busy");

  const toggleFavourite = useMutation({
    mutationFn: async () => {
      if (isFavourite) {
        await apiRequest('DELETE', `/api/favourites/${venue!.id}`);
      } else {
        await apiRequest('POST', `/api/favourites/${venue!.id}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favourites', venue?.id, 'check'] });
      queryClient.invalidateQueries({ queryKey: ['/api/favourites'] });
      toast({
        title: isFavourite ? "Removed from Favourites" : "Added to Favourites",
        description: isFavourite ? `${venue!.name} removed` : `${venue!.name} saved to your favourites`,
      });
    },
  });

  const saveFavouriteWithPrefs = useMutation({
    mutationFn: async (prefs: { eventsEnabled: boolean; thresholdEnabled: boolean; thresholdType: "quiet" | "busy" | "both" }) => {
      await apiRequest('POST', `/api/favourites/${venue!.id}`);
      await apiRequest('POST', `/api/venues/${venue!.id}/notification-prefs`, prefs);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favourites', venue?.id, 'check'] });
      queryClient.invalidateQueries({ queryKey: ['/api/favourites'] });
      setPrefsDrawerOpen(false);
      toast({
        title: "Added to Favourites",
        description: `${venue!.name} saved to your favourites`,
      });
    },
    onError: () => {
      toast({
        title: "Couldn't save favourite",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });

  function handleHeartClick() {
    if (!venue) return;
    if (isFavourite) {
      toggleFavourite.mutate();
      return;
    }
    setPrefsEvents(true);
    setPrefsThreshold(false);
    setPrefsThresholdType("busy");
    setPrefsDrawerOpen(true);
  }

  function handlePrefsConfirm() {
    saveFavouriteWithPrefs.mutate({
      eventsEnabled: prefsEvents,
      thresholdEnabled: prefsThreshold,
      thresholdType: prefsThresholdType,
    });
  }

  function handlePrefsSkip() {
    saveFavouriteWithPrefs.mutate({
      eventsEnabled: true,
      thresholdEnabled: false,
      thresholdType: "busy",
    });
  }

  useEffect(() => {
    if (isOpen && venue && venue.id > 0 && venue.id !== lastTrackedId.current) {
      lastTrackedId.current = venue.id;
      fetch(`/api/venues/${venue.id}/view`, { method: "POST" }).catch(() => {});
    }
    if (!isOpen) {
      lastTrackedId.current = null;
    }
  }, [isOpen, venue]);

  async function handleSubmitReport() {
    if (!venue) return;
    setLocationError(null);

    let userLat: number;
    let userLng: number;
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 8000,
        });
      });
      userLat = pos.coords.latitude;
      userLng = pos.coords.longitude;
    } catch {
      setLocationError("Location access is required to Report the Vibe. Please enable location permissions.");
      return;
    }

    setReportSubmitting(true);
    try {
      const res = await fetch(`/api/venues/${venue.id}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ capacityLevel: reportCapacity, vibe: reportVibe, userLat, userLng }),
      });
      if (!res.ok) {
        const err = await res.json();
        setLocationError(err.message);
        return;
      }
      const data = await res.json();

      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("mediaType", mediaType);
        formData.append("userLat", userLat.toString());
        formData.append("userLng", userLng.toString());
        await fetch(`/api/venues/${venue.id}/media`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/venues"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["/api/business/venues", venue.id, "analytics"] });
      queryClient.invalidateQueries({ queryKey: ["/api/venues", venue.id, "photos"] });
      queryClient.invalidateQueries({ queryKey: ["/api/honey/balance"] });

      clearMedia();
      setShowReport(false);

      toast({
        title: "Vibe reported! +10 Honey 🍯",
        description: `Your new balance is ${data.newBalance} Honey`,
      });
    } finally {
      setReportSubmitting(false);
    }
  }

  type VenuePhotoResponse = {
    id: number;
    url: string;
    widthPx: number | null;
    heightPx: number | null;
    attributions: string | null;
    source: "google" | "user";
  };

  type UserMediaItem = {
    id: number;
    userId: number;
    username: string;
    mediaType: "photo" | "video";
    cloudinaryUrl: string;
    thumbnailUrl: string | null;
    durationSeconds: number | null;
    thumbsUp: number;
    thumbsDown: number;
    createdAt: string;
  };

  const { data: venuePhotos, isLoading: photosLoading } = useQuery<VenuePhotoResponse[]>({
    queryKey: ['/api/venues', venue?.id, 'photos'],
    enabled: !!venue,
  });

  const { data: userMediaData } = useQuery<{ media: UserMediaItem[] }>({
    queryKey: ['/api/venues', venue?.id, 'media'],
    queryFn: () => fetch(`/api/venues/${venue!.id}/media`, { credentials: 'include' }).then(r => r.json()),
    enabled: !!venue,
  });

  const [selectedUserMedia, setSelectedUserMedia] = useState<UserMediaItem | null>(null);
  const [userReactions, setUserReactions] = useState<Record<number, "thumbs_up" | "thumbs_down" | null>>({});
  const [userMediaCounts, setUserMediaCounts] = useState<Record<number, { thumbsUp: number; thumbsDown: number }>>({});

  const [failedPhotoIds, setFailedPhotoIds] = useState<Set<number>>(new Set());
  const [expandedPhotoIndex, setExpandedPhotoIndex] = useState<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef<number>(0);
  const photoRetryRef = useRef(false);

  useEffect(() => {
    setFailedPhotoIds(new Set());
    setShowHours(false);
    setShowMapsPicker(false);
    setExpandedPhotoIndex(null);
    setSelectedUserMedia(null);
    setUserReactions({});
    setUserMediaCounts({});
    setSelectedFile(null);
    setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setVideoDuration(null);
    setVideoError(null);
    photoRetryRef.current = false;
  }, [venue?.id]);

  const allPhotos = venuePhotos ?? [];
  const visiblePhotos = allPhotos.filter(p => !failedPhotoIds.has(p.id));

  const allPhotosFailed = allPhotos.length > 0 && visiblePhotos.length === 0;

  useEffect(() => {
    if (!allPhotosFailed || photoRetryRef.current) return;
    photoRetryRef.current = true;
    const timer = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['/api/venues', venue?.id, 'photos'], exact: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [allPhotosFailed, venue?.id, queryClient]);

  if (!venue) return null;

  const isSponsored = venue.isSponsored;

  const venueClosed = (() => {
    const hours = venue.openingHours as SharedOpeningHoursData | null;
    if (hours?.periods && hours.periods.length > 0) {
      return !isVenueOpen(hours);
    }
    return (venue as any).isCurrentlyOpen === false;
  })();

  const noData = hasNoCapacityData(venue);
  const capacitySource =
    (venue as Venue & { source?: CapacitySource | null }).source ?? null;

  const capPct = (venue as any).capacityPercent ?? venue.capacityLevel ?? 0;
  const iconForKind = (kind: ReturnType<typeof getCapacityIconKind>) => {
    switch (kind) {
      case "flame-pulse": return <Flame className="w-5 h-5 mr-2 animate-pulse" />;
      case "flame":       return <Flame className="w-5 h-5 mr-2" />;
      case "users":       return <Users className="w-5 h-5 mr-2" />;
      case "users-muted": return <Users className="w-5 h-5 mr-2 opacity-60" />;
    }
  };
  let statusColor = getCapacityLabelTextClass(capPct);
  let statusText = getCapacityLabel(capPct);
  let statusIcon = iconForKind(getCapacityIconKind(capPct));

  if (venueClosed) {
    statusColor = "text-zinc-500";
    statusText = "Closed";
    statusIcon = <Ban className="w-5 h-5 mr-2" />;
  } else if (noData) {
    statusColor = "text-zinc-500";
    statusText = "No Data";
    statusIcon = <Users className="w-5 h-5 mr-2 opacity-40" />;
  }

  // Type Icon
  const typeLower = (venue.type ?? '').toLowerCase();
  const TypeIcon =
    typeLower === 'bar' ? GlassWater :
    typeLower === 'pub' ? GlassWater :
    typeLower === 'club' ? Music :
    GlassWater;

  return (
    <>
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} onAnimationEnd={onAnimationEnd} modal={false}>
      <DrawerContent className={`bg-background border-zinc-800 text-white max-w-md mx-auto rounded-t-[32px] h-[calc(95vh-95px)] max-h-[calc(95vh-95px)] transition-all duration-300 z-[2100] focus:outline-none ${isSponsored ? 'border-t-primary border-x-primary border-t-2 border-x-2' : ''}`}>
        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-zinc-800 my-4" />
        <div className="flex-1 overflow-y-auto scrollbar-hide p-6 pb-8">
          {isSponsored && (
            <div className="flex items-center gap-2 bg-primary/15 border border-primary/30 rounded-xl px-4 py-2.5 mb-4" data-testid="banner-hotspot-sponsored">
              <span className="text-base">🍯</span>
              <span className="text-sm font-semibold text-primary tracking-wide">Buzzn Sponsored</span>
            </div>
          )}
          {/* Header — v4 meta row + title block */}
          <div className="space-y-3 mb-6">
            {/* Meta row: type, price, updated, heart */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border bg-transparent text-zinc-300 capitalize px-2.5 py-0.5 text-[11px] font-semibold rounded-lg">
                <TypeIcon className="w-3 h-3 mr-1" />
                {venue.type}
              </Badge>
              <PriceMeter priceLevel={venue.googlePriceLevel} />
              {venue.lastUpdated && !venueClosed && (
                <span className="text-[11px] text-zinc-500 flex items-center gap-1 ml-auto">
                  <Clock className="w-3 h-3" />
                  Updated just now
                </span>
              )}
              <Button
                data-testid="button-toggle-favourite"
                variant="ghost"
                size="icon"
                className={`shrink-0 w-9 h-9 rounded-xl border ${venue.lastUpdated && !venueClosed ? '' : 'ml-auto'} ${isFavourite ? 'bg-primary/10 border-primary hover:bg-primary/15' : 'border-border hover:bg-white/5'}`}
                onClick={handleHeartClick}
                disabled={toggleFavourite.isPending || saveFavouriteWithPrefs.isPending}
              >
                <Heart className={`w-4 h-4 transition-colors ${isFavourite ? 'fill-primary text-primary' : 'text-zinc-500'}`} />
              </Button>
            </div>

            {/* Title block + capacity badge */}
            <div className="flex items-start justify-between gap-3">
              {venue.logoUrl && (
                <img
                  src={venue.logoUrl}
                  alt={`${venue.name} logo`}
                  className="w-14 h-14 rounded-xl object-cover border border-zinc-800 shrink-0"
                  data-testid="img-venue-logo"
                  loading="lazy"
                />
              )}
              <div className="flex-1 min-w-0">
                <DrawerTitle className="text-[26px] leading-tight font-display font-bold tracking-tight mb-1 break-words">
                  {venue.name}
                </DrawerTitle>
                <DrawerDescription className="text-zinc-400 text-sm">
                  {venue.description || `${venue.type} in London.`}
                </DrawerDescription>
              </div>
              <div className={`flex items-center justify-center w-16 h-16 rounded-2xl border shrink-0 ${
                venueClosed || noData
                  ? 'bg-card border-border text-zinc-500'
                  : `bg-primary/10 border-primary/30 ${statusColor}`
              }`}>
                {venueClosed ? (
                  <Ban className="w-6 h-6" />
                ) : noData ? (
                  <span className="text-xl font-bold">—</span>
                ) : (
                  <span className="text-[22px] font-bold leading-none">{(venue as any).capacityPercent ?? venue.capacityLevel ?? 0}%</span>
                )}
              </div>
            </div>
          </div>

          {/* 1. Capacity meter */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className={`flex items-center font-medium ${statusColor}`}>
                {statusIcon}
                {statusText}
              </span>
              <span className="text-sm text-zinc-500">Capacity</span>
            </div>
            {venueClosed ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center" data-testid="text-venue-closed">
                <Ban className="w-5 h-5 text-zinc-500 mx-auto mb-2" />
                <p className="text-sm text-zinc-400 font-medium">This venue is currently closed</p>
                <p className="text-xs text-zinc-600 mt-1">Capacity data will update when it reopens</p>
              </div>
            ) : noData ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center" data-testid="text-no-capacity-data">
                <Users className="w-5 h-5 text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-400 font-medium">No capacity data available</p>
                <p className="text-xs text-zinc-600 mt-1">Be the first to report the vibe!</p>
              </div>
            ) : (
              <>
                <Progress value={(venue as any).capacityPercent ?? venue.capacityLevel ?? 0} className="h-3 bg-zinc-800" />
                {(() => {
                  const recentReportCount = (venue as any).recentReportCount ?? 0;
                  if (recentReportCount > 0) {
                    return (
                      <p className="text-xs text-primary mt-2 font-medium" data-testid="text-recent-reports">
                        Based on {recentReportCount} recent {recentReportCount === 1 ? "report" : "reports"}
                      </p>
                    );
                  }
                  if (capacitySource === "claude_estimate") {
                    return (
                      <p className="text-xs text-zinc-500 mt-2 italic" data-testid="text-claude-estimate">
                        Weekly Claude estimate — modelled pattern, not live foot traffic
                      </p>
                    );
                  }
                  if (capacitySource === "serpapi_histogram") {
                    return (
                      <p className="text-xs text-zinc-500 mt-2" data-testid="text-serpapi-histogram">
                        Based on the venue&apos;s popular-times pattern
                      </p>
                    );
                  }
                  if (venue.reportCount > 0) {
                    return (
                      <p className="text-xs text-zinc-500 mt-2" data-testid="text-crowd-report-capacity">
                        Based on recent crowd reports from visitors
                      </p>
                    );
                  }
                  if (venue.reportCount === 0 && (venue.googleRating || venue.googleRatingCount)) {
                    return (
                      <p className="text-xs text-zinc-500 mt-2 italic" data-testid="text-estimated-capacity">
                        Estimated from rating data — no live tracking or recent reports
                      </p>
                    );
                  }
                  return null;
                })()}
              </>
            )}
          </div>

          {/* Opening Hours */}
          {(() => {
            const hoursData = venue.openingHours as OpeningHoursData | null;
            const status = getOpenClosedStatus(hoursData);
            if (!status) return null;
            return (
              <div className="mb-6" data-testid="opening-hours-section">
                <button
                  onClick={() => setShowHours(!showHours)}
                  className="flex items-center gap-2 w-full text-left"
                  data-testid="button-toggle-hours"
                >
                  <Clock className="w-4 h-4 text-zinc-400" />
                  <span className={`text-sm font-medium ${status.isOpen ? 'text-emerald-400' : 'text-red-400'}`}>
                    {status.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${showHours ? 'rotate-180' : ''}`} />
                </button>
                {showHours && hoursData?.weekdayDescriptions && (
                  <div className="mt-3 space-y-1.5 pl-6" data-testid="opening-hours-list">
                    {hoursData.weekdayDescriptions.map((desc, i) => {
                      const today = new Date().getDay();
                      const dayIndex = (i + 1) % 7;
                      const isToday = dayIndex === today;
                      return (
                        <p key={i} className={`text-xs ${isToday ? 'text-white font-medium' : 'text-zinc-500'}`}>
                          {desc}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Events & Specials */}
          <div className="space-y-4 mb-8" data-testid="events-section">
            <h4 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Events & Specials
            </h4>
            {eventsLoading && (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/10 animate-pulse">
                    <div className="h-4 w-20 bg-zinc-700 rounded mb-2" />
                    <div className="h-3 w-48 bg-zinc-800 rounded" />
                  </div>
                ))}
              </div>
            )}
            {specials && specials.length > 0 && (
              <div className="space-y-2">
                {specials.map((special: any) => {
                  const isFree = special.honeyCost === 0;
                  const canAfford = isFree || (honeyBalance ?? 0) >= special.honeyCost;
                  const shortfall = special.honeyCost - (honeyBalance ?? 0);
                  return (
                    <div
                      key={special.id}
                      className={`p-3 rounded-xl border transition-opacity ${canAfford ? 'bg-primary/10 border-primary/20' : 'bg-white/5 border-white/10 opacity-50'}`}
                      data-testid={`special-card-${special.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-white">{special.title}</span>
                            <span className={`text-[10px] font-medium px-1.5 py-0 rounded-full border ${canAfford ? 'bg-primary/20 text-primary border-primary/30' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
                              {isFree ? 'Free' : `⬡ ${special.honeyCost}`}
                            </span>
                          </div>
                          {special.description && (
                            <p className="text-xs text-zinc-500 mt-0.5">{special.description}</p>
                          )}
                        </div>
                        {isFree ? (
                          <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-7 px-3 flex-shrink-0"
                            onClick={() => redeemMutation.mutate(special.id)}
                            disabled={redeemMutation.isPending}
                            data-testid={`button-redeem-${special.id}`}
                          >
                            Redeem
                          </Button>
                        ) : canAfford ? (
                          <Button
                            size="sm"
                            className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-7 px-3 flex-shrink-0"
                            onClick={() => redeemMutation.mutate(special.id)}
                            disabled={redeemMutation.isPending}
                            data-testid={`button-redeem-${special.id}`}
                          >
                            Redeem ⬡ {special.honeyCost}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-500/30 text-zinc-500 text-xs h-7 px-3 flex-shrink-0 cursor-not-allowed"
                            disabled
                            data-testid={`button-redeem-${special.id}`}
                          >
                            Need {shortfall} more
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!eventsLoading && events && events.length > 0 && (
              <div className="space-y-2">
                {DAY_ORDER
                  .filter(day => events.some(e => e.dayOfWeek === day))
                  .map(day => {
                    const dayEvents = events.filter(e => e.dayOfWeek === day);
                    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
                    const isToday = day === today;
                    return (
                      <div key={day} className={`p-3 rounded-xl border ${isToday ? 'bg-white/10 border-white/20' : 'bg-white/5 border-white/10'}`} data-testid={`event-day-${day.toLowerCase()}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-sm font-bold ${isToday ? 'text-white' : 'text-zinc-400'}`}>{day}</span>
                          {isToday && <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-400 border-0 text-[10px] px-1.5 py-0">Today</Badge>}
                        </div>
                        {dayEvents.map(event => (
                          <div key={event.id} className="flex items-start justify-between gap-2 mt-1.5 first:mt-0" data-testid={`event-item-${event.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-white">{event.title}</span>
                                {event.tag && (
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border ${TAG_COLORS[event.tag] || 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
                                    {event.tag}
                                  </Badge>
                                )}
                              </div>
                              {event.description && <p className="text-xs text-zinc-500 mt-0.5">{event.description}</p>}
                            </div>
                            <span className="text-xs text-zinc-500 whitespace-nowrap flex-shrink-0">{event.time}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
              </div>
            )}
            {!eventsLoading && (!events || events.length === 0) && (!specials || specials.length === 0) && (
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center" data-testid="events-empty">
                <p className="text-sm text-zinc-400">On the way</p>
                <p className="text-xs text-zinc-600 mt-1">Events & specials will appear here once added</p>
              </div>
            )}
          </div>

          {/* Upcoming Events — one-off events from RA + business posts */}
          {upcomingEvents && upcomingEvents.length > 0 && (
            <div className="space-y-3 mb-8" data-testid="upcoming-events-section">
              <h4 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                Upcoming Events
              </h4>
              <div className="space-y-2">
                {upcomingEvents.map((ev) => (
                  <div
                    key={ev.id}
                    data-testid={`upcoming-event-${ev.id}`}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate" data-testid={`upcoming-event-title-${ev.id}`}>
                        {ev.title}
                      </p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {formatUpcomingDateTime(ev.startTime)}
                      </p>
                    </div>
                    {ev.ticketUrl && (
                      <a
                        data-testid={`upcoming-event-tickets-${ev.id}`}
                        href={ev.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary transition-colors flex-shrink-0 inline-flex items-center"
                      >
                        Tickets & More Info
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Take a Peek — merged user media + Google photos */}
          {(() => {
            const userItems = userMediaData?.media ?? [];
            const hasAny = userItems.length > 0 || visiblePhotos.length > 0 || photosLoading;
            if (!hasAny) return null;
            return (
              <div className="space-y-4 mb-8" data-testid="photos-section">
                <h4 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Take a Peek
                </h4>
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-2 px-2">
                  {/* User-submitted photos and videos — newest first */}
                  {userItems.map((item) => {
                    const thumb = item.mediaType === "video" ? item.thumbnailUrl : item.cloudinaryUrl;
                    return (
                      <button
                        key={`user-${item.id}`}
                        className="relative flex-shrink-0 w-36 rounded-xl overflow-hidden group border border-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30"
                        data-testid={`media-item-${item.id}`}
                        aria-label={`${item.mediaType} by @${item.username}`}
                        onClick={() => {
                          setSelectedUserMedia(item);
                          setUserMediaCounts(prev => ({
                            ...prev,
                            [item.id]: { thumbsUp: item.thumbsUp, thumbsDown: item.thumbsDown },
                          }));
                        }}
                      >
                        <div className="w-full h-44 bg-zinc-800">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={`${item.mediaType} by @${item.username}`}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-zinc-800">
                              <Video className="w-8 h-8 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        {item.mediaType === "video" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-black/50 border border-white/30 flex items-center justify-center">
                              <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                            </div>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5">
                          <p className="text-[10px] text-white/80 truncate">
                            <span
                              data-testid={`link-user-${item.userId}`}
                              role="link"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); onUserClick?.(item.userId); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onUserClick?.(item.userId); } }}
                              className="hover:underline cursor-pointer"
                            >@{item.username}</span>
                            {" · "}{timeAgo(item.createdAt)}
                          </p>
                        </div>
                      </button>
                    );
                  })}

                  {/* Google photos — existing behaviour */}
                  {allPhotosFailed
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div
                          key={`skeleton-${i}`}
                          className="relative flex-shrink-0 w-36 h-48 rounded-xl overflow-hidden border border-white/10 bg-white/5 animate-pulse"
                        />
                      ))
                    : visiblePhotos.map((photo, index) => (
                        <button
                          key={`google-${photo.id}`}
                          className="relative flex-shrink-0 w-36 h-48 rounded-xl overflow-hidden group border border-white/10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/30"
                          data-testid={`photo-item-${photo.id}`}
                          aria-label="Expand venue photo"
                          onClick={() => setExpandedPhotoIndex(index)}
                        >
                          <img
                            src={photo.url}
                            alt="Venue photo from Google Reviews"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={() => setFailedPhotoIds(prev => new Set([...Array.from(prev), photo.id]))}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <ZoomIn className="w-4 h-4 text-white drop-shadow-md" />
                          </div>
                        </button>
                      ))
                  }
                </div>
                <p className="text-xs text-zinc-500 text-center" data-testid="text-photo-honey-hint">
                  Upload a photo — earn <span className="text-primary font-bold">+5 Honey 🍯</span>
                </p>
              </div>
            );
          })()}

          {/* Social Media Links */}
          <div className="space-y-4 mb-8" data-testid="social-links-section">
            <h4 className="text-sm font-semibold text-zinc-500 uppercase tracking-wider flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Follow {venue.name}
            </h4>
            {(venue.instagramUrl || venue.tiktokUrl || venue.websiteUrl) ? (
              <div className="flex gap-2 flex-wrap">
                {venue.instagramUrl && (
                  <a
                    href={venue.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-instagram"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-transform hover:scale-105"
                    style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}
                  >
                    <SiInstagram className="w-4 h-4" />
                    Instagram
                  </a>
                )}
                {venue.tiktokUrl && (
                  <a
                    href={venue.tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-tiktok"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-black border border-zinc-700 transition-transform hover:scale-105"
                  >
                    <SiTiktok className="w-4 h-4" />
                    TikTok
                  </a>
                )}
                {venue.websiteUrl && (
                  <a
                    href={venue.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="link-website"
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-zinc-800 border border-zinc-700 transition-transform hover:scale-105"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                  </a>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-center" data-testid="social-links-empty">
                <p className="text-sm text-zinc-400">Social links on the way</p>
                <p className="text-xs text-zinc-600 mt-1">The venue team will add their socials here shortly</p>
              </div>
            )}
          </div>

          {/* Action bar — v4 stacked CTAs */}
          <div className="space-y-2.5 pt-2">
            <Button
              data-testid="button-report-vibe"
              variant="outline"
              className="w-full h-12 rounded-2xl border-border text-white font-semibold gap-2 bg-transparent hover:bg-white/5"
              onClick={() => setShowReport(true)}
            >
              <Flame className="w-4 h-4 text-primary" />
              Report the Vibe
            </Button>
            <Button
              className="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-2"
              data-testid="button-take-me-there"
              onClick={() => setShowMapsPicker(true)}
            >
              <MapPin className="w-5 h-5" />
              Take me there
            </Button>
          </div>

          {showMapsPicker && (
            <div
              className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60"
              data-testid="modal-maps-picker"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowMapsPicker(false);
              }}
            >
              <div className="w-full max-w-sm mx-4 mb-6 bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden">
                <div className="px-4 pt-4 pb-2">
                  <h3 className="text-white font-semibold text-center text-lg">Open directions in…</h3>
                </div>
                <div className="flex flex-col gap-2 px-4 pb-2">
                  <a
                    className="w-full h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-medium inline-flex items-center justify-center transition-colors"
                    data-testid="button-apple-maps"
                    href={`https://maps.apple.com/?daddr=${venue.latitude},${venue.longitude}&dirflg=d`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMapsPicker(false)}
                  >
                    Apple Maps
                  </a>
                  <a
                    className="w-full h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-medium inline-flex items-center justify-center transition-colors"
                    data-testid="button-google-maps"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMapsPicker(false)}
                  >
                    Google Maps
                  </a>
                  <a
                    className="w-full h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-medium inline-flex items-center justify-center transition-colors"
                    data-testid="button-citymapper"
                    href={`https://citymapper.com/directions?endcoord=${venue.latitude},${venue.longitude}&endname=${encodeURIComponent(venue.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMapsPicker(false)}
                  >
                    Citymapper
                  </a>
                </div>
                <div className="px-4 pb-4">
                  <Button
                    variant="outline"
                    className="w-full h-10 rounded-xl border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                    data-testid="button-maps-picker-cancel"
                    onClick={() => setShowMapsPicker(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* User media full-screen modal */}
        {selectedUserMedia && (() => {
          const item = selectedUserMedia;
          const counts = userMediaCounts[item.id] ?? { thumbsUp: item.thumbsUp, thumbsDown: item.thumbsDown };
          const myReaction = userReactions[item.id] ?? null;

          async function handleReact(reaction: "thumbs_up" | "thumbs_down") {
            if (!isAuthenticated) return;
            const prev = userReactions[item.id] ?? null;
            const prevCounts = userMediaCounts[item.id] ?? { thumbsUp: item.thumbsUp, thumbsDown: item.thumbsDown };
            let newCounts = { ...prevCounts };
            let newReaction: "thumbs_up" | "thumbs_down" | null = reaction;

            if (prev === reaction) {
              // toggle off
              newReaction = null;
              newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
            } else {
              if (prev) {
                newCounts[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, newCounts[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
              }
              newCounts[reaction === "thumbs_up" ? "thumbsUp" : "thumbsDown"] += 1;
            }

            setUserReactions(r => ({ ...r, [item.id]: newReaction }));
            setUserMediaCounts(c => ({ ...c, [item.id]: newCounts }));

            try {
              await fetch(`/api/media/${item.id}/react`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ reaction }),
              });
            } catch {
              // revert on failure
              setUserReactions(r => ({ ...r, [item.id]: prev }));
              setUserMediaCounts(c => ({ ...c, [item.id]: prevCounts }));
            }
          }

          return (
            <div
              className="fixed inset-0 z-[3000] bg-black/95 flex flex-col items-center justify-center"
              data-testid="user-media-modal"
              onClick={() => setSelectedUserMedia(null)}
            >
              <button
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                data-testid="button-close-media-modal"
                aria-label="Close"
                onClick={(e) => { e.stopPropagation(); setSelectedUserMedia(null); }}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="w-full max-w-sm px-4" onClick={(e) => e.stopPropagation()}>
                {item.mediaType === "photo" ? (
                  <img
                    src={item.cloudinaryUrl}
                    alt={`Photo by @${item.username}`}
                    className="w-full max-h-[70vh] object-contain rounded-xl"
                    data-testid="media-modal-photo"
                  />
                ) : (
                  <video
                    src={item.cloudinaryUrl}
                    className="w-full max-h-[70vh] rounded-xl"
                    autoPlay
                    muted
                    playsInline
                    loop
                    data-testid="media-modal-video"
                  />
                )}

                <div className="mt-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-zinc-300 truncate">
                    <span
                      data-testid={`link-user-modal-${item.userId}`}
                      role="link"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onUserClick?.(item.userId); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onUserClick?.(item.userId); } }}
                      className="text-white font-medium hover:underline cursor-pointer"
                    >@{item.username}</span>
                    {" "}shared this {timeAgo(item.createdAt)}
                  </p>
                  {isAuthenticated && (
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <button
                        data-testid="button-thumbs-up"
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${myReaction === "thumbs_up" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-white/10 text-zinc-300 border border-white/10 hover:bg-white/20"}`}
                        onClick={() => handleReact("thumbs_up")}
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>{counts.thumbsUp}</span>
                      </button>
                      <button
                        data-testid="button-thumbs-down"
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${myReaction === "thumbs_down" ? "bg-red-500/20 text-red-400 border border-red-500/40" : "bg-white/10 text-zinc-300 border border-white/10 hover:bg-white/20"}`}
                        onClick={() => handleReact("thumbs_down")}
                      >
                        <ThumbsDown className="w-4 h-4" />
                        <span>{counts.thumbsDown}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {expandedPhotoIndex !== null && visiblePhotos.length > 0 && (
          <div
            className="fixed inset-0 z-[3000] bg-black/90 flex items-center justify-center"
            data-testid="photo-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`Photo ${expandedPhotoIndex + 1} of ${visiblePhotos.length}`}
            onClick={() => setExpandedPhotoIndex(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setExpandedPhotoIndex(null);
              if (e.key === 'ArrowLeft' && expandedPhotoIndex > 0) setExpandedPhotoIndex(expandedPhotoIndex - 1);
              if (e.key === 'ArrowRight' && expandedPhotoIndex < visiblePhotos.length - 1) setExpandedPhotoIndex(expandedPhotoIndex + 1);
            }}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
              touchDeltaX.current = 0;
            }}
            onTouchMove={(e) => {
              if (touchStartX.current !== null) {
                touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
              }
            }}
            onTouchEnd={() => {
              if (Math.abs(touchDeltaX.current) > 50) {
                if (touchDeltaX.current < 0 && expandedPhotoIndex < visiblePhotos.length - 1) {
                  setExpandedPhotoIndex(expandedPhotoIndex + 1);
                } else if (touchDeltaX.current > 0 && expandedPhotoIndex > 0) {
                  setExpandedPhotoIndex(expandedPhotoIndex - 1);
                }
              }
              touchStartX.current = null;
              touchDeltaX.current = 0;
            }}
            tabIndex={-1}
            ref={(el) => el?.focus()}
          >
            <button
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
              data-testid="button-close-lightbox"
              aria-label="Close photo"
              onClick={(e) => { e.stopPropagation(); setExpandedPhotoIndex(null); }}
            >
              <X className="w-5 h-5" />
            </button>

            {expandedPhotoIndex > 0 && (
              <button
                className="absolute left-3 z-10 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                data-testid="button-lightbox-prev"
                aria-label="Previous photo"
                onClick={(e) => { e.stopPropagation(); setExpandedPhotoIndex(expandedPhotoIndex - 1); }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}

            {expandedPhotoIndex < visiblePhotos.length - 1 && (
              <button
                className="absolute right-3 z-10 w-10 h-10 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
                data-testid="button-lightbox-next"
                aria-label="Next photo"
                onClick={(e) => { e.stopPropagation(); setExpandedPhotoIndex(expandedPhotoIndex + 1); }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            )}

            <img
              src={visiblePhotos[expandedPhotoIndex].url}
              alt={`Venue photo ${expandedPhotoIndex + 1} of ${visiblePhotos.length}`}
              className="max-w-full max-h-[85vh] object-contain rounded-xl px-14"
              onClick={(e) => e.stopPropagation()}
            />

            <div className="absolute bottom-6 left-0 right-0 text-center space-y-2">
              <div className="flex justify-center gap-1.5">
                {visiblePhotos.map((_, i) => (
                  <button
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all ${i === expandedPhotoIndex ? 'bg-white w-4' : 'bg-white/40'}`}
                    onClick={(e) => { e.stopPropagation(); setExpandedPhotoIndex(i); }}
                    aria-label={`Go to photo ${i + 1}`}
                    data-testid={`lightbox-dot-${i}`}
                  />
                ))}
              </div>
              {visiblePhotos[expandedPhotoIndex].attributions && (
                <span className="text-xs text-white/60 bg-black/50 px-3 py-1 rounded-full">
                  {visiblePhotos[expandedPhotoIndex].attributions}
                </span>
              )}
            </div>
          </div>
        )}
      </DrawerContent>
    </Drawer>

    {/* Report the Vibe — v4 dedicated bottom sheet */}
    <Drawer
      open={showReport}
      onOpenChange={(open) => {
        if (!open && !reportSubmitting) {
          setShowReport(false);
          clearMedia();
          setLocationError(null);
        }
      }}
    >
      <DrawerContent
        className="bg-background border-zinc-800 text-white max-w-md mx-auto rounded-t-[32px] z-[2100] focus:outline-none max-h-[88vh] flex flex-col"
        data-testid="drawer-report-vibe"
      >
        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-zinc-800 my-4" />
        <DrawerHeader className="text-left pt-0 px-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <DrawerTitle className="text-xl font-bold" data-testid="text-report-title">
                Report the Vibe
              </DrawerTitle>
              <DrawerDescription className="text-zinc-500 text-xs mt-0.5 truncate">
                {venue?.name}
              </DrawerDescription>
            </div>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 border border-primary/30 shrink-0">
              <span className="text-sm">🍯</span>
              <span className="text-xs font-bold text-primary">+15</span>
            </span>
          </div>
        </DrawerHeader>

        <div className="px-5 pb-8 space-y-3 overflow-y-auto scrollbar-hide">
          {/* How busy is it? */}
          <div className="bg-card rounded-2xl p-5 border border-border">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-bold text-white">How busy is it?</span>
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-lg border ${getCapacityChipClass(reportCapacity)}`}
                data-testid="text-busy-label"
              >
                {getCapacityLabel(reportCapacity)}
              </span>
            </div>
            <input
              data-testid="input-report-capacity"
              type="range"
              min="0"
              max="100"
              value={reportCapacity}
              onChange={(e) => setReportCapacity(Number(e.target.value))}
              className="w-full accent-primary"
            />
            <div className="flex justify-between mt-2">
              <span className="text-[11px] text-zinc-500">Dead quiet</span>
              <span className="text-[11px] text-zinc-500">Absolutely rammed</span>
            </div>
          </div>

          {/* What's the vibe? */}
          <div className="bg-card rounded-2xl p-5 border border-border">
            <p className="text-sm font-bold text-white mb-3">What's the vibe?</p>
            <div className="flex flex-wrap gap-2">
              {VIBE_OPTIONS.map((vibe) => (
                <button
                  key={vibe}
                  data-testid={`button-vibe-${vibe.toLowerCase()}`}
                  onClick={() => setReportVibe(vibe)}
                  className={`text-xs font-semibold px-3.5 py-2 rounded-full border transition-colors ${
                    reportVibe === vibe
                      ? 'bg-primary/10 border-primary text-primary'
                      : 'bg-transparent border-border text-zinc-300 hover:bg-white/5'
                  }`}
                >
                  {vibe}
                </button>
              ))}
            </div>
          </div>

          {/* Add a photo or video — required */}
          <div className="bg-card rounded-2xl p-5 border border-border">
            <div className="flex justify-between items-center mb-1">
              <p className="text-sm font-bold text-white">Add a photo or video</p>
              <span className="text-[11px] text-primary font-semibold">required</span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">You must be at the venue to upload</p>

            {/* Media type toggle */}
            <div className="flex gap-1 mb-3 p-0.5 bg-background rounded-lg border border-border">
              <button
                data-testid="toggle-media-photo"
                className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${
                  mediaType === 'photo'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => {
                  setMediaType('photo');
                  clearMedia();
                }}
              >
                <Camera className="w-3.5 h-3.5" />
                Photo
              </button>
              <button
                data-testid="toggle-media-video"
                className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-1.5 rounded-md transition-colors ${
                  mediaType === 'video'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                onClick={() => {
                  setMediaType('video');
                  clearMedia();
                }}
              >
                <Video className="w-3.5 h-3.5" />
                Video
              </button>
            </div>

            {/* Hidden inputs */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="input-report-photo"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setSelectedFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              capture="environment"
              className="hidden"
              data-testid="input-report-video"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setVideoError(null);
                setVideoDuration(null);
                setSelectedFile(file);
                setPreviewUrl(URL.createObjectURL(file));
              }}
            />

            {/* Hidden video element for duration validation */}
            <video
              ref={videoRef}
              src={mediaType === 'video' && previewUrl ? previewUrl : undefined}
              className="hidden"
              muted
              playsInline
              onLoadedMetadata={() => {
                const vid = videoRef.current;
                if (!vid) return;
                const dur = vid.duration;
                setVideoDuration(dur);
                if (dur < 3) setVideoError('Video must be at least 3 seconds');
                else if (dur > 10) setVideoError('Video must be 10 seconds or less');
                else setVideoError(null);
              }}
            />

            {previewUrl ? (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border">
                <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-border flex-shrink-0">
                  {mediaType === 'photo' ? (
                    <img src={previewUrl} alt="Selected photo" className="w-full h-full object-cover" />
                  ) : (
                    <video
                      src={previewUrl}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                    />
                  )}
                  <button
                    data-testid="button-clear-media"
                    className="absolute top-0.5 right-0.5 bg-black/70 rounded-full p-0.5"
                    onClick={clearMedia}
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  {videoError ? (
                    <span className="text-xs text-red-400">{videoError}</span>
                  ) : (
                    <span className="text-xs text-zinc-300">
                      {mediaType === 'photo' ? 'Photo' : 'Video'} ready to upload
                    </span>
                  )}
                  {mediaType === 'video' && videoDuration !== null && !videoError && (
                    <span className="text-xs text-zinc-500">{Math.round(videoDuration)}s</span>
                  )}
                </div>
              </div>
            ) : (
              <button
                data-testid="button-add-media"
                onClick={() =>
                  mediaType === 'photo'
                    ? photoInputRef.current?.click()
                    : videoInputRef.current?.click()
                }
                className="w-full border border-dashed border-border rounded-xl py-6 flex flex-col items-center gap-2 hover:bg-white/5 transition-colors"
              >
                {mediaType === 'photo' ? (
                  <Camera className="w-7 h-7 text-zinc-600" />
                ) : (
                  <Video className="w-7 h-7 text-zinc-600" />
                )}
                <span className="text-xs text-zinc-500">
                  Tap to add {mediaType === 'photo' ? 'a photo' : 'a video'} (required)
                </span>
              </button>
            )}
          </div>

          {locationError && (
            <p className="text-red-400 text-xs text-center" data-testid="text-location-error">
              {locationError}
            </p>
          )}

          <Button
            data-testid="button-submit-report"
            onClick={handleSubmitReport}
            disabled={reportSubmitting || !selectedFile || (mediaType === 'video' && !!videoError)}
            className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base disabled:opacity-50"
          >
            {reportSubmitting ? 'Sending…' : !selectedFile ? 'Add a photo or video to submit' : 'Submit Vibe →'}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>

    <Drawer open={prefsDrawerOpen} onOpenChange={(open) => !saveFavouriteWithPrefs.isPending && setPrefsDrawerOpen(open)}>
      <DrawerContent className="bg-background border-zinc-800 text-white max-w-md mx-auto rounded-t-[32px] z-[2100] focus:outline-none" data-testid="drawer-notification-prefs">
        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-zinc-800 my-4" />
        <DrawerHeader className="text-left">
          <DrawerTitle className="text-xl" data-testid="text-prefs-title">
            Notifications for {venue?.name ?? "this venue"}
          </DrawerTitle>
          <DrawerDescription className="text-zinc-400">
            Choose what you'd like to be notified about
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-6 pb-6 space-y-5">
          <div className="flex items-center justify-between gap-4 rounded-2xl bg-white/5 border border-white/10 px-4 py-3">
            <Label htmlFor="prefs-events" className="text-base font-medium leading-snug cursor-pointer flex-1">
              Events &amp; specials updated
            </Label>
            <Switch
              id="prefs-events"
              checked={prefsEvents}
              onCheckedChange={setPrefsEvents}
              data-testid="switch-events-enabled"
            />
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 space-y-3">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="prefs-threshold" className="text-base font-medium leading-snug cursor-pointer flex-1">
                Notify me when it gets quiet/busy
              </Label>
              <Switch
                id="prefs-threshold"
                checked={prefsThreshold}
                onCheckedChange={setPrefsThreshold}
                data-testid="switch-threshold-enabled"
              />
            </div>
            {prefsThreshold && (
              <RadioGroup
                value={prefsThresholdType}
                onValueChange={(v) => setPrefsThresholdType(v as "quiet" | "busy" | "both")}
                className="grid grid-cols-3 gap-2 pt-1"
                data-testid="radiogroup-threshold-type"
              >
                {[
                  { value: "quiet", label: "Quiet (<50%)" },
                  { value: "busy", label: "Busy (>59%)" },
                  { value: "both", label: "Both" },
                ].map((opt) => (
                  <Label
                    key={opt.value}
                    htmlFor={`prefs-threshold-${opt.value}`}
                    className={`flex items-center justify-center text-center text-sm rounded-xl border px-2 py-2 cursor-pointer transition-colors ${
                      prefsThresholdType === opt.value
                        ? "bg-primary/15 border-primary/60 text-primary"
                        : "bg-white/0 border-white/10 text-zinc-300 hover:bg-white/5"
                    }`}
                    data-testid={`label-threshold-${opt.value}`}
                  >
                    <RadioGroupItem
                      id={`prefs-threshold-${opt.value}`}
                      value={opt.value}
                      className="sr-only"
                    />
                    {opt.label}
                  </Label>
                ))}
              </RadioGroup>
            )}
          </div>
        </div>

        <DrawerFooter className="pt-0">
          <Button
            onClick={handlePrefsConfirm}
            disabled={saveFavouriteWithPrefs.isPending}
            className="w-full bg-primary hover:bg-primary text-primary-foreground font-semibold rounded-2xl h-12"
            data-testid="button-save-favourite"
          >
            {saveFavouriteWithPrefs.isPending ? "Saving…" : "Save & Favourite"}
          </Button>
          <button
            type="button"
            onClick={handlePrefsSkip}
            disabled={saveFavouriteWithPrefs.isPending}
            className="text-sm text-zinc-400 hover:text-zinc-200 underline-offset-2 hover:underline mx-auto py-2 disabled:opacity-50"
            data-testid="button-skip-prefs"
          >
            Skip
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
    </>
  );
}
